
import { Helmet } from "react-helmet";
import { RedirectUploader } from "@/components/admin/RedirectUploader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useState, useEffect } from "react";
import { RedirectRule } from "@/types";
import { Plus, Save, Trash2, RefreshCw, Download, History, Zap } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { updateRedirectRules, normalizePath, reloadRedirects } from "@/utils/redirects";
import { clearRedirectCache, getCacheStats } from "@/utils/redirectUtils";

const Redirects = () => {
  const [rules, setRules] = useState<RedirectRule[]>([]);
  const [newFrom, setNewFrom] = useState("");
  const [newTo, setNewTo] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isTesting, setIsTesting] = useState(false);
  const [testPath, setTestPath] = useState("");
  const [testResult, setTestResult] = useState<any>(null);
  const [redirectStats, setRedirectStats] = useState<{total: number, active: number}>({
    total: 0,
    active: 0
  });
  const [cacheStats, setCacheStats] = useState<{size: number, hitRate: number}>({
    size: 0,
    hitRate: 0
  });
  const { toast } = useToast();

  // Load existing redirect rules when component mounts
  useEffect(() => {
    loadRules();
    updateCacheStats();
  }, []);
  
  const updateCacheStats = () => {
    const stats = getCacheStats();
    setCacheStats({
      size: stats.size,
      hitRate: stats.cacheHitRate
    });
  };

  const loadRules = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('redirect_rules')
        .select('from_path, to_path, notes, created_at, active')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('[RedirectAdmin] Error loading rules:', error);
        toast({
          title: "Error loading redirects",
          description: "There was a problem loading your redirect rules.",
          variant: "destructive",
        });
        return;
      }

      if (data) {
        setRules(data.map(rule => ({
          from: rule.from_path,
          to: rule.to_path,
          notes: rule.notes,
          active: rule.active,
          createdAt: rule.created_at
        })));
        
        const activeCount = data.filter(rule => rule.active).length;
        setRedirectStats({
          total: data.length,
          active: activeCount
        });
        
        console.log(`[RedirectAdmin] Loaded ${data.length} rules (${activeCount} active)`);
      }
    } catch (err) {
      console.error('[RedirectAdmin] Error:', err);
      toast({
        title: "Error loading redirects",
        description: "There was a problem loading your redirect rules.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveRules = async () => {
    if (rules.length === 0) {
      toast({
        title: "No redirects to save",
        description: "Please add at least one redirect rule first.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsSaving(true);
      const success = await updateRedirectRules(rules);
      
      if (success) {
        // Force reload of redirect data from database after saving
        await reloadRedirects();
        
        // Clear client-side cache as well
        clearRedirectCache();
        updateCacheStats();
        
        toast({
          title: "Redirects saved",
          description: `${redirectStats.active} redirect rule${redirectStats.active === 1 ? '' : 's'} have been saved and activated.`,
        });
      } else {
        toast({
          title: "Error saving redirects",
          description: "There was a problem saving your redirect rules.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("[RedirectAdmin] Error saving redirects:", error);
      toast({
        title: "Error saving redirects",
        description: "There was a problem saving your redirect rules.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddRule = () => {
    if (!newFrom || !newTo) {
      toast({
        title: "Error",
        description: "Both 'From' and 'To' fields are required.",
        variant: "destructive",
      });
      return;
    }

    if (!newFrom.startsWith("/") || !newTo.startsWith("/")) {
      toast({
        title: "Error",
        description: "Both paths must start with a '/' character.",
        variant: "destructive",
      });
      return;
    }

    // Normalize paths
    const normalizedFrom = normalizePath(newFrom);
    const normalizedTo = normalizePath(newTo);

    const exists = rules.some(rule => rule.from === normalizedFrom);
    if (exists) {
      toast({
        title: "Error",
        description: `A redirect rule for '${normalizedFrom}' already exists.`,
        variant: "destructive",
      });
      return;
    }

    // Check for redirect loops
    if (normalizedFrom === normalizedTo) {
      toast({
        title: "Error",
        description: `Cannot redirect a path to itself.`,
        variant: "destructive",
      });
      return;
    }
    
    // Check for potential redirect chains (simplified check)
    const potentialDestination = rules.find(rule => rule.from === normalizedTo);
    if (potentialDestination) {
      toast({
        title: "Warning",
        description: `The destination '${normalizedTo}' is itself redirected to '${potentialDestination.to}'. This will create a redirect chain.`,
      });
    }

    const updatedRules = [...rules, { 
      from: normalizedFrom, 
      to: normalizedTo, 
      active: true,
      notes: "",
      createdAt: new Date().toISOString()
    }];
    setRules(updatedRules);
    setNewFrom("");
    setNewTo("");
    
    // Update stats
    setRedirectStats(prev => ({
      total: prev.total + 1,
      active: prev.active + 1
    }));
    
    toast({
      title: "Rule added",
      description: `Added redirect from ${normalizedFrom} to ${normalizedTo}`,
    });
  };

  const handleToggleRule = (index: number) => {
    const updatedRules = [...rules];
    updatedRules[index].active = !updatedRules[index].active;
    setRules(updatedRules);
    
    // Update stats
    setRedirectStats(prev => ({
      ...prev,
      active: updatedRules[index].active ? prev.active + 1 : prev.active - 1
    }));
    
    toast({
      title: updatedRules[index].active ? "Rule activated" : "Rule deactivated",
      description: `The redirect rule has been ${updatedRules[index].active ? 'activated' : 'deactivated'}.`,
    });
  };

  const handleDeleteRule = (index: number) => {
    const wasActive = rules[index].active;
    const updatedRules = [...rules];
    updatedRules.splice(index, 1);
    setRules(updatedRules);
    
    // Update stats
    setRedirectStats(prev => ({
      total: prev.total - 1,
      active: wasActive ? prev.active - 1 : prev.active
    }));
    
    toast({
      title: "Rule deleted",
      description: "The redirect rule has been removed.",
    });
  };

  const handleUpdateRuleNotes = (index: number, notes: string) => {
    const updatedRules = [...rules];
    updatedRules[index].notes = notes;
    setRules(updatedRules);
  };

  const handleBulkUpdate = (newRules: RedirectRule[]) => {
    setRules(newRules);
    
    // Update stats
    const activeCount = newRules.filter(rule => rule.active !== false).length;
    setRedirectStats({
      total: newRules.length,
      active: activeCount
    });
  };
  
  const downloadRedirectsCSV = () => {
    // Only include active redirects in the export
    const activeRules = rules.filter(rule => rule.active !== false);
    
    if (activeRules.length === 0) {
      toast({
        title: "No active redirects",
        description: "There are no active redirect rules to export.",
        variant: "destructive",
      });
      return;
    }
    
    // Create CSV content
    const headers = "from,to,notes\n";
    const csvContent = headers + activeRules.map(rule => 
      `${rule.from},${rule.to},${rule.notes?.replace(/,/g, ' ') || ''}`
    ).join("\n");
    
    // Create download link
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `redirects-${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast({
      title: "Redirects exported",
      description: `${activeRules.length} active redirect rules have been exported to CSV.`,
    });
  };
  
  const handleClearCache = () => {
    clearRedirectCache();
    updateCacheStats();
    toast({
      title: "Redirect cache cleared",
      description: "The client-side redirect cache has been cleared.",
    });
  };
  
  const handleTestRedirect = async () => {
    if (!testPath) {
      toast({
        title: "Error",
        description: "Please enter a path to test.",
        variant: "destructive",
      });
      return;
    }
    
    setIsTesting(true);
    setTestResult(null);
    
    try {
      // Normalize the path
      const normalizedPath = normalizePath(testPath);
      
      // We'll use the API directly since testRedirect is no longer available
      const { data, error } = await supabase.functions.invoke('unified-redirector', {
        body: { path: normalizedPath },
      });
      
      const result = {
        success: !error,
        result: data?.Location ? { Location: data.Location, statusCode: data.statusCode || 301 } : null,
        source: 'API'
      };
      
      setTestResult(result);
      
      if (result.success && result.result?.Location) {
        toast({
          title: "Redirect found",
          description: `Path would redirect to: ${result.result.Location}`,
        });
      } else if (result.success) {
        toast({
          title: "No redirect found",
          description: "This path does not have a redirect rule.",
        });
      } else {
        toast({
          title: "Error testing redirect",
          description: error?.message || 'Unknown error',
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('[RedirectAdmin] Error testing redirect:', error);
      setTestResult({
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
        source: 'client'
      });
      
      toast({
        title: "Error testing redirect",
        description: "There was a problem testing the redirect.",
        variant: "destructive",
      });
    } finally {
      setIsTesting(false);
    }
  };

  return (
    <div className="space-y-6">
      <Helmet>
        <title>Manage Redirects | edgefi Admin</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Redirects</h1>
          <p className="text-gray-500">Manage URL redirects for your website</p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={loadRules}
            variant="outline"
            className="flex items-center gap-2"
            disabled={isLoading}
          >
            <RefreshCw size={16} className={isLoading ? "animate-spin" : ""} />
            {isLoading ? "Loading..." : "Refresh"}
          </Button>
          <Button 
            onClick={handleClearCache}
            variant="outline"
            className="flex items-center gap-2"
          >
            <History size={16} />
            Clear Cache
          </Button>
          <Button 
            onClick={downloadRedirectsCSV}
            variant="outline"
            className="flex items-center gap-2"
            disabled={redirectStats.active === 0}
          >
            <Download size={16} />
            Export CSV
          </Button>
          <Button 
            onClick={handleSaveRules} 
            className="flex items-center gap-2"
            disabled={isSaving || redirectStats.active === 0}
          >
            <Save size={16} />
            {isSaving ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <div>
          <div className="rounded-lg border p-4 bg-white">
            <h2 className="text-lg font-semibold mb-4">Add New Redirect</h2>
            <div className="grid gap-4">
              <div>
                <label htmlFor="from-path" className="text-sm font-medium block mb-1">
                  From Path
                </label>
                <Input
                  id="from-path"
                  value={newFrom}
                  onChange={e => setNewFrom(e.target.value)}
                  placeholder="/old-path"
                />
                <p className="text-xs text-gray-500 mt-1">
                  The original path that should be redirected
                </p>
              </div>
              
              <div>
                <label htmlFor="to-path" className="text-sm font-medium block mb-1">
                  To Path
                </label>
                <Input
                  id="to-path"
                  value={newTo}
                  onChange={e => setNewTo(e.target.value)}
                  placeholder="/new-path"
                />
                <p className="text-xs text-gray-500 mt-1">
                  The destination path where users will be redirected
                </p>
              </div>
              
              <Button onClick={handleAddRule} className="w-full flex items-center justify-center gap-2">
                <Plus size={16} />
                Add Redirect
              </Button>
            </div>
          </div>

          <div className="mt-6">
            <RedirectUploader onRulesUpdate={handleBulkUpdate} />
          </div>
          
          <div className="mt-6 rounded-lg border p-4 bg-white">
            <h2 className="text-lg font-semibold mb-4">Test Redirect</h2>
            <div className="grid gap-4">
              <div>
                <label htmlFor="test-path" className="text-sm font-medium block mb-1">
                  Test Path
                </label>
                <div className="flex gap-2">
                  <Input
                    id="test-path"
                    value={testPath}
                    onChange={e => setTestPath(e.target.value)}
                    placeholder="/path-to-test"
                    className="flex-1"
                  />
                  <Button 
                    onClick={handleTestRedirect}
                    disabled={isTesting || !testPath}
                    className="flex items-center gap-2"
                  >
                    <Zap size={16} />
                    {isTesting ? "Testing..." : "Test"}
                  </Button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Test if a path has a redirect rule
                </p>
              </div>
              
              {testResult && (
                <div className="p-3 bg-gray-50 rounded-md border text-sm font-mono whitespace-pre-wrap">
                  {JSON.stringify(testResult, null, 2)}
                </div>
              )}
            </div>
            
            <div className="mt-4 text-sm text-gray-500">
              <h3 className="font-medium mb-1">Redirect Cache Stats</h3>
              <div className="flex gap-4">
                <div>Size: {cacheStats.size} entries</div>
                <div>Hit rate: {cacheStats.hitRate.toFixed(2)}%</div>
              </div>
            </div>
          </div>
          
          <div className="mt-6 rounded-lg border p-4 bg-white">
            <h2 className="text-lg font-semibold mb-4">Redirect Best Practices</h2>
            <ul className="list-disc pl-5 space-y-2 text-sm text-gray-700">
              <li>
                <strong>Use 301 redirects</strong> for permanent URL changes (this system uses 301s by default)
              </li>
              <li>
                <strong>Keep redirects simple</strong> - avoid redirect chains for better performance and SEO
              </li>
              <li>
                <strong>Update internal links</strong> rather than relying solely on redirects
              </li>
              <li>
                <strong>Monitor 404 errors</strong> to identify pages that may need redirects
              </li>
              <li>
                <strong>Use consistent URL formats</strong> - decide on trailing slashes and stick with it
              </li>
            </ul>
          </div>
        </div>

        <div className="rounded-lg border bg-white">
          <div className="p-4 border-b">
            <h2 className="text-lg font-semibold">Current Redirects ({redirectStats.total})</h2>
            <p className="text-sm text-gray-500">
              {redirectStats.active} active redirect {redirectStats.active === 1 ? 'rule' : 'rules'} for your website
            </p>
          </div>
          
          <div className="max-h-[600px] overflow-y-auto">
            {isLoading ? (
              <div className="p-8 text-center">
                <p>Loading redirect rules...</p>
              </div>
            ) : rules.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <p>No redirect rules have been added yet.</p>
                <p className="text-sm mt-1">Add your first redirect using the form on the left.</p>
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-3">
                      Status
                    </th>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-3">
                      From
                    </th>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-3">
                      To
                    </th>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-3">
                      Notes
                    </th>
                    <th className="text-right text-xs font-medium text-gray-500 uppercase tracking-wider px-6 py-3">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {rules.map((rule, index) => (
                    <tr key={index} className={rule.active !== false ? "hover:bg-gray-50" : "hover:bg-gray-50 bg-gray-50 text-gray-400"}>
                      <td className="px-6 py-4">
                        <span 
                          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                            rule.active !== false 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {rule.active !== false ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm font-mono">
                        {rule.from}
                      </td>
                      <td className="px-6 py-4 text-sm font-mono">
                        {rule.to}
                      </td>
                      <td className="px-6 py-4">
                        <Input
                          value={rule.notes || ''}
                          onChange={(e) => handleUpdateRuleNotes(index, e.target.value)}
                          placeholder="Add notes (optional)"
                          className="text-sm h-7 py-1"
                        />
                      </td>
                      <td className="px-6 py-4 text-right whitespace-nowrap">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleToggleRule(index)}
                            className={rule.active !== false ? "text-amber-600" : "text-green-600"}
                          >
                            {rule.active !== false ? 'Disable' : 'Enable'}
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleDeleteRule(index)}
                            className="text-red-600 hover:text-red-800 hover:bg-red-50"
                          >
                            <Trash2 size={16} />
                            <span className="sr-only">Delete</span>
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Redirects;
